import { useState, useMemo } from 'react';
import { EmptyStateCard } from '../EmptyStateCard';
import { HeatmapGrid, HeatmapModeToggle, type HeatmapMode, type HeatmapColumn, type HeatmapRow } from './HeatmapGrid';
import { useGetAssetComparisonQuery, useGetAssetsQuery } from '../../api/emissions';
import { getAssetTypeBrand } from '../../config/assetTypeBranding';
import type { AssetType } from '../../types/emissions';
import { SCOPE_COLORS } from '../../config/scopeBranding';
import { SCOPE_COLUMNS, ACTIVITY_COLUMNS, buildActivityValuesFromBreakdown } from './heatmapColumns';

interface AssetHeatmapPanelProps {
  initiativeId: string;
  periodStart: string;
  periodEnd: string;
  assetType?: AssetType | 'unassigned';
  includeChildren?: boolean;
}

export const AssetHeatmapPanel = ({ initiativeId, periodStart, periodEnd, assetType, includeChildren }: AssetHeatmapPanelProps) => {
  const [mode, setMode] = useState<HeatmapMode>('activity');
  const { data: comparisonData, isLoading: comparisonLoading } = useGetAssetComparisonQuery({ initiativeId, periodStart, periodEnd, includeChildren });
  const { data: allAssetsResponse, isLoading: assetsLoading } = useGetAssetsQuery({ initiativeId, limit: 100, includeChildren });
  const isLoading = comparisonLoading || assetsLoading;

  const columns: HeatmapColumn[] = useMemo(() => {
    if (mode === 'scope') {
      return SCOPE_COLUMNS.map((c) => ({ key: c.key, label: c.label, color: SCOPE_COLORS[c.key] }));
    }
    return ACTIVITY_COLUMNS.map((c) => ({ key: c.key, label: c.label }));
  }, [mode]);

  // Show ALL assets + unassigned, filling zero-emission placeholders for missing assets
  const rows: HeatmapRow[] = useMemo(() => {
    const comparisonAssets = comparisonData?.assets ?? [];
    const allAssets = allAssetsResponse?.data ?? [];
    const comparisonMap = new Map(comparisonAssets.map((a) => [a._id, a]));

    const items: Array<HeatmapRow & { assetType: string }> = allAssets.map((asset) => {
      const comparison = comparisonMap.get(asset._id);
      const brand = getAssetTypeBrand(asset.assetType);

      const scopeMap: Record<string, number> = {};
      for (const s of comparison?.scopes ?? []) {
        scopeMap[s.scope] = s.co2e;
      }

      return {
        id: asset._id,
        label: (
          <div className='tw:flex tw:items-center tw:gap-1'>
            {brand && <brand.Icon className={`tw:w-3 tw:h-3 tw:shrink-0 ${brand.iconColor}`} />}
            <span className='tw:truncate'>{asset.name}</span>
          </div>
        ),
        values: mode === 'scope' ? scopeMap : buildActivityValuesFromBreakdown(comparison?.activityTypes ?? []),
        total: comparison?.totalCo2e ?? 0,
        assetType: asset.assetType,
      };
    });

    // Include "Unassigned" entry from comparison data if present
    const unassigned = comparisonAssets.find((a) => a._id === 'unassigned');
    if (unassigned) {
      const scopeMap: Record<string, number> = {};
      for (const s of unassigned.scopes) { scopeMap[s.scope] = s.co2e; }
      items.push({
        id: 'unassigned',
        label: <span className='tw:truncate tw:text-muted-foreground'>Unassigned</span>,
        values: mode === 'scope' ? scopeMap : buildActivityValuesFromBreakdown(unassigned.activityTypes ?? []),
        total: unassigned.totalCo2e,
        assetType: 'unassigned',
      });
    }

    // Sort: emitting first (desc), then zero alphabetically
    items.sort((a, b) => {
      if (a.total > 0 && b.total === 0) return -1;
      if (a.total === 0 && b.total > 0) return 1;
      if (a.total > 0 && b.total > 0) return b.total - a.total;
      return 0;
    });

    if (!assetType) return items;
    if (assetType === 'unassigned') return items.filter((a) => a.id === 'unassigned');
    const filtered = items.filter((a) => a.assetType === assetType);
    return filtered;
  }, [comparisonData?.assets, allAssetsResponse?.data, assetType, mode]);

  if (!isLoading && rows.length === 0) {
    return (
      <EmptyStateCard
        title='Asset Heatmap'
        message='No assets found. Create assets to see the heatmap.'
      />
    );
  }

  return (
    <HeatmapGrid
      title={mode === 'scope' ? 'Asset × Scope' : 'Asset × Activity Type'}
      description='Color intensity represents emission magnitude. Empty cells indicate no data for that combination.'
      columns={columns}
      rows={rows}
      isLoading={isLoading}
      headerExtra={<HeatmapModeToggle value={mode} onChange={setMode} />}
    />
  );
};
